import React, { useState, useEffect } from 'react';
import { LinkIcon, PlusIcon, PencilIcon, TrashIcon, StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import api from '../../../services/api';

const QuickLinksWidget = () => {
  const [bookmarks, setBookmarks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchBookmarks();
    fetchCategories();
  }, [selectedCategory]);

  const fetchBookmarks = async () => {
    try {
      const params = selectedCategory !== 'all' ? `?category=${selectedCategory}` : '';
      const response = await api.get(`/portal/bookmarks${params}`);
      setBookmarks(response.data);
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/portal/bookmarks/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const togglePin = async (bookmarkId) => {
    try {
      await api.patch(`/portal/bookmarks/${bookmarkId}/pin`);
      fetchBookmarks();
    } catch (error) {
      console.error('Error toggling pin:', error);
    }
  };

  const deleteBookmark = async (bookmarkId) => {
    if (!window.confirm('Are you sure you want to delete this bookmark?')) return;

    try {
      await api.delete(`/portal/bookmarks/${bookmarkId}`);
      fetchBookmarks();
      fetchCategories();
    } catch (error) {
      console.error('Error deleting bookmark:', error);
    }
  };

  if (loading) {
    return (
      <div className="portal-widget h-full">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  // Group bookmarks by category
  const groupedBookmarks = bookmarks.reduce((acc, bookmark) => {
    const category = bookmark.category || 'Uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(bookmark);
    return acc;
  }, {});

  // Sort: pinned first
  Object.keys(groupedBookmarks).forEach(category => {
    groupedBookmarks[category].sort((a, b) => {
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;
      return a.sort_order - b.sort_order;
    });
  });

  return (
    <div className="portal-widget h-full flex flex-col">
      <div className="portal-widget-header flex items-center justify-between">
        <h3 className="portal-widget-title flex items-center">
          <LinkIcon className="h-5 w-5 mr-2" />
          Quick Links
        </h3>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary px-3 py-1 text-sm flex items-center"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Add
        </button>
      </div>

      {/* Category Filter */}
      {categories.length > 0 && (
        <div className="mb-4 flex space-x-2 overflow-x-auto pb-2">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${
              selectedCategory === 'all'
                ? 'bg-blue-600 text-white'
                : 'theme-bg-secondary theme-text-secondary hover:bg-blue-100'
            }`}
          >
            All ({bookmarks.length})
          </button>
          {categories.map((cat) => (
            <button
              key={cat.category}
              onClick={() => setSelectedCategory(cat.category)}
              className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${
                selectedCategory === cat.category
                  ? 'bg-blue-600 text-white'
                  : 'theme-bg-secondary theme-text-secondary hover:bg-blue-100'
              }`}
            >
              {cat.category} ({cat.count})
            </button>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto space-y-4 max-h-96">
        {bookmarks.length === 0 ? (
          <div className="text-center py-8 theme-text-secondary">
            <LinkIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No bookmarks yet</p>
            <p className="text-sm mt-1">Add your favorite links for quick access</p>
          </div>
        ) : (
          Object.keys(groupedBookmarks).map((category) => (
            <div key={category}>
              {selectedCategory === 'all' && (
                <h4 className="text-sm font-semibold theme-text-secondary mb-2 uppercase">
                  {category}
                </h4>
              )}
              <div className="grid grid-cols-1 gap-2">
                {groupedBookmarks[category].map((bookmark) => (
                  <div
                    key={bookmark.id}
                    className="theme-bg-secondary rounded-lg p-3 hover:shadow-md transition-all group"
                  >
                    <div className="flex items-center justify-between">
                      <a
                        href={bookmark.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-3 flex-1 min-w-0"
                      >
                        <span className="text-2xl flex-shrink-0">
                          {bookmark.icon || '🔗'}
                        </span>
                        <div className="flex-1 min-w-0">
                          <h5 className="font-medium theme-text-primary truncate flex items-center">
                            {bookmark.title}
                            {bookmark.is_pinned && (
                              <StarSolidIcon className="h-4 w-4 text-yellow-500 ml-2" />
                            )}
                          </h5>
                          {bookmark.description && (
                            <p className="text-xs theme-text-tertiary truncate">
                              {bookmark.description}
                            </p>
                          )}
                          <p className="text-xs theme-text-tertiary truncate mt-1">
                            {bookmark.url}
                          </p>
                        </div>
                      </a>

                      <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => togglePin(bookmark.id)}
                          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                          title={bookmark.is_pinned ? 'Unpin' : 'Pin'}
                        >
                          {bookmark.is_pinned ? (
                            <StarSolidIcon className="h-4 w-4 text-yellow-500" />
                          ) : (
                            <StarIcon className="h-4 w-4 theme-text-tertiary" />
                          )}
                        </button>
                        <button
                          onClick={() => deleteBookmark(bookmark.id)}
                          className="p-1 hover:bg-red-100 dark:hover:bg-red-900 rounded"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4 text-red-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {showCreateModal && (
        <CreateBookmarkModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            fetchBookmarks();
            fetchCategories();
          }}
        />
      )}
    </div>
  );
};

// Create Bookmark Modal
const CreateBookmarkModal = ({ onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    title: '',
    url: '',
    description: '',
    category: '',
    icon: '🔗'
  });
  const [saving, setSaving] = useState(false);

  const commonIcons = ['🔗', '📄', '📊', '📧', '🏠', '⚙️', '📁', '🌐', '💼', '📚', '🔍', '📱'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await api.post('/portal/bookmarks', formData);
      onCreated();
      onClose();
    } catch (error) {
      console.error('Error creating bookmark:', error);
      alert('Failed to create bookmark');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-content max-w-2xl">
          <div className="modal-header">
            <h2 className="text-xl font-semibold theme-text-primary">Add Quick Link</h2>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body space-y-4">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="form-input"
                  placeholder="Enter link title"
                />
              </div>

              <div className="form-group">
                <label>URL *</label>
                <input
                  type="url"
                  required
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  className="form-input"
                  placeholder="https://example.com"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="form-input"
                  placeholder="Optional description"
                />
              </div>

              <div className="form-group">
                <label>Category</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="form-input"
                  placeholder="e.g., Work, Personal, Tools"
                />
              </div>

              <div className="form-group">
                <label>Icon</label>
                <div className="flex items-center space-x-2">
                  <span className="text-3xl">{formData.icon}</span>
                  <div className="flex flex-wrap gap-2">
                    {commonIcons.map((icon) => (
                      <button
                        key={icon}
                        type="button"
                        onClick={() => setFormData({ ...formData, icon })}
                        className={`text-2xl p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                          formData.icon === icon ? 'bg-blue-100 dark:bg-blue-900' : ''
                        }`}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Adding...' : 'Add Link'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QuickLinksWidget;
